import { Color } from "@deck.gl/core";
import { GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import { Feature } from "geojson";
import centerOfMass from "@turf/center-of-mass";

import {
  EarlyWarningTableWidgetDataWarningsInner,
  EarlyWarningTableWidgetDataWarningsInnerLevelEnum,
  EarlyWarningTableWidgetDataWarningsInnerPhenomenonEnum,
} from "@/lib/api";
import FogMarker from "@/assets/images/icons-with-background/phenomena_fog.png";
import HighTempMarker from "@/assets/images/icons-with-background/phenomena_high-temperature.png";
import LowTempMarker from "@/assets/images/icons-with-background/phenomena_low-temperature.png";
import RainMarker from "@/assets/images/icons-with-background/phenomena_rain.png";
import SnowIceMarker from "@/assets/images/icons-with-background/phenomena_snow-ice.png";
import ThunderMarker from "@/assets/images/icons-with-background/phenomena_thunderstorm.png";
import WindMarker from "@/assets/images/icons-with-background/phenomena_wind.png";

export const getColor = (
  level?: EarlyWarningTableWidgetDataWarningsInnerLevelEnum,
): Color => {
  switch (level) {
    case "yellow":
      // Remove later when new color is tested and approved
      // return [249, 225, 0] as [number, number, number];
      return [255, 238, 0] as [number, number, number];
    case "orange":
      // Remove later when new color is tested and approved
      // return [225, 112, 0] as [number, number, number];
      return [255, 153, 0] as [number, number, number];
    case "red":
    default:
      // Remove later when new color is tested and approved
      // return [213, 43, 30] as [number, number, number];
      return [205, 0, 0] as [number, number, number];
  }
};

export const getPercentage = (percentage?: number): string | undefined => {
  if (!percentage) return undefined;

  if (percentage < 30) return "10-30%";
  if (percentage >= 30 && percentage < 60) return "30-60%";
  if (percentage >= 60 && percentage < 90) return "60-90%";
  return "90-100%";
};

export const getGeoJsonLayerFromWarning = (
  warning: EarlyWarningTableWidgetDataWarningsInner,
  selectedWarningId?: string,
) => {
  return new GeoJsonLayer({
    id: warning.id,
    slot: "middle",
    data: warning.area as Feature,
    getFillColor: () => getColor(warning.level),
    opacity: selectedWarningId ? (selectedWarningId === warning.id ? 0.5 : 0) : 0.5,
  });
};

const getIcon = (phenomenon: EarlyWarningTableWidgetDataWarningsInnerPhenomenonEnum) => {
  switch (phenomenon) {
    case "fog":
      return FogMarker;
    case "high-temperature":
      return HighTempMarker;
    case "low-temperature":
      return LowTempMarker;
    case "rain":
    case "rain-flood":
    case "flooding":
      return RainMarker;
    case "snow-ice":
      return SnowIceMarker;
    case "thunderstorm":
      return ThunderMarker;
    case "wind":
      return WindMarker;
    default:
      return undefined;
  }
};

export const getIconLayerFromWarning = (
  warnings: EarlyWarningTableWidgetDataWarningsInner[],
  selectedWarningId: string | undefined,
  setSelectedWarning?: (id: string) => void,
) => {
  return new IconLayer({
    id: "warning-icons",
    data: warnings,
    getPosition: (warning) => {
      const center = centerOfMass(warning.area).geometry.coordinates;
      return [center[0], center[1]];
    },
    getIcon: (warning) => {
      const url = getIcon(warning.phenomenon);
      return {
        url: url ?? "",
        height: 256,
        width: 256,
        anchorX: 128,
        anchorY: 128,
      };
    },
    getColor: (warning) => [
      0,
      0,
      0,
      selectedWarningId ? (warning.id === selectedWarningId ? 255 : 0.25 * 255) : 255,
    ],
    getSize: 24,
    pickable: true,
    onClick: (info) => setSelectedWarning?.(info.object.id),
  });
};

export const sortWarningsByLevel = (
  warnings: EarlyWarningTableWidgetDataWarningsInner[],
) => {
  return warnings.sort(
    (a, b) => warningLevelAsInteger(a.level) - warningLevelAsInteger(b.level),
  );
};

const warningLevelAsInteger = (
  level?: EarlyWarningTableWidgetDataWarningsInnerLevelEnum,
) => {
  switch (level) {
    case EarlyWarningTableWidgetDataWarningsInnerLevelEnum.Yellow:
      return 1;
    case EarlyWarningTableWidgetDataWarningsInnerLevelEnum.Orange:
      return 2;
    case EarlyWarningTableWidgetDataWarningsInnerLevelEnum.Red:
      return 3;
    default:
      return 0;
  }
};
